import {
  createContext,
  useReducer,
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
  type ReactNode,
} from 'react';
import type {
  EditorState,
  EditorAction,
  EditorSnapshot,
  Layer,
  ManualTool,
  AdjustmentState,
  Product,
  ProductLayersPayload,
  HorizontalAlignment,
  VerticalAlignment,
  DistributeDirection,
} from '@/types';
import type { Side } from './sideContext';
import type { Orientation } from '@/types/orientation.types';
import { useAutoSave, type SaveStatus } from '@/hooks/useAutoSave';
import { useSyncOnLoad } from '@/hooks/useSyncOnLoad';
import { useAuthSafe } from './useAuth';
import { saveAutoSave, getLatestAutoSave } from '@/services/storage';
import { CANVAS_WIDTH, CANVAS_HEIGHT } from '@/utils/canvasConstants';
import { resolveProductImage } from '@/utils/productFrames';

// ============================================
// SessionStorage Keys
// ============================================

// SessionStorage keys for persisting editor state during auth flows
const EDITOR_STATE_FRONT_KEY = 'editor_state_front';
const EDITOR_STATE_BACK_KEY = 'editor_state_back';
const EDITOR_PRODUCT_ID_KEY = 'editor_product_id';
const EDITOR_ORIENTATION_KEY = 'editor_orientation';

// ============================================
// Initial State
// ============================================

// Shared canvas state (zoom/pan/grid/guides) - applies to both front and back
const initialSharedCanvasState = {
  zoom: 100,
  panX: 0,
  panY: 0,
  showGrid: false,
  snapToGrid: false,
  showGuides: false,
};

// Per-side canvas state (only width/height, which may differ between sides)
const initialCanvasState = {
  width: 800,
  height: 600,
};

const initialAdjustments: AdjustmentState = {
  brightness: 0,
  contrast: 0,
  inverted: false,
  sharpen: 0,
  blur: 0,
  opacity: 100,
};

// Initial state for the reducer (per-side state)
// Note: Canvas zoom/pan/grid in this state are placeholders - they get overridden by shared state
const initialState: EditorState = {
  layers: [],
  activeLayerId: null,
  selectedLayerIds: [],
  activeTool: 'select',
  canvas: {
    ...initialCanvasState,
    ...initialSharedCanvasState, // Include placeholders for TypeScript, but these will be overridden
  },
  adjustments: initialAdjustments,
  penSize: 5,
  eraserSize: 10,
  penColor: 'black',
  history: {
    past: [],
    future: [],
  },
};

// ============================================
// Helper Functions
// ============================================

/**
 * Load editor state from sessionStorage
 * Returns null if no saved state, state is corrupted, or product changed
 */
function loadStateFromSession(key: string, currentProductId: string): EditorState | null {
  try {
    const saved = sessionStorage.getItem(key);
    if (!saved) return null;

    const parsed = JSON.parse(saved) as EditorState;

    // Validate that parsed state has required structure
    if (typeof parsed !== 'object' || parsed === null) return null;

    // Check if the saved state is for the current product
    const savedProductId = sessionStorage.getItem(EDITOR_PRODUCT_ID_KEY);
    if (savedProductId !== currentProductId) {
      // Product changed - clear old state
      sessionStorage.removeItem(key);
      return null;
    }

    // Restore any data URLs that were stripped when saving to sessionStorage
    if (parsed.layers) {
      parsed.layers = restoreStrippedDataUrls(parsed.layers);
    }

    return parsed;
  } catch (error) {
    console.warn('[EditorContext] Failed to load state from session storage:', error);
    return null;
  }
}

/**
 * In-memory cache for large image data URLs stripped from sessionStorage.
 * Persists across component mount/unmount cycles (e.g., mode switching)
 * but NOT across page refreshes (which is acceptable for session data).
 * Keyed by layer ID.
 */
const imageDataUrlCache = new Map<string, string>();

/**
 * Strip large base64 data URLs from layers to avoid sessionStorage quota issues.
 * sessionStorage has a ~5MB limit, and AI-generated images are ~1-2MB each as base64.
 * Stripped images are cached in memory so they can be restored on remount.
 */
function stripLargeDataUrls(layers: Layer[]): Layer[] {
  return layers.map((layer) => {
    // Only process image layers with data URLs
    if (layer.data.type !== 'image') return layer;

    const src = layer.data.src;
    // Skip if not a data URL or if it's a small one (< 10KB is ~13KB base64)
    if (!src || !src.startsWith('data:') || src.length < 15000) return layer;

    // Cache the data URL in memory before stripping
    imageDataUrlCache.set(layer.id, src);

    return {
      ...layer,
      data: {
        ...layer.data,
        src: 'stripped:data-url-too-large',
      },
    };
  });
}

/**
 * Restore stripped data URLs from in-memory cache.
 * Called when loading state from sessionStorage after a mode switch.
 */
function restoreStrippedDataUrls(layers: Layer[]): Layer[] {
  return layers.map((layer) => {
    if (layer.data.type !== 'image') return layer;
    if (layer.data.src !== 'stripped:data-url-too-large') return layer;

    const cached = imageDataUrlCache.get(layer.id);
    if (!cached) return layer;

    return {
      ...layer,
      data: {
        ...layer.data,
        src: cached,
      },
    };
  });
}

/**
 * Check if any image layers have stripped data URL markers.
 * These markers indicate images were too large for sessionStorage and need
 * to be restored from IndexedDB auto-save.
 */
function hasStrippedImages(layers: Layer[]): boolean {
  return layers.some(
    (layer) => layer.data.type === 'image' && layer.data.src === 'stripped:data-url-too-large'
  );
}

/**
 * Restore stripped image data URLs from an auto-save snapshot.
 * Matches layers by ID and copies the full image src from the snapshot.
 * Returns a new layers array (immutable).
 */
function restoreImagesFromSnapshot(currentLayers: Layer[], snapshotLayers: Layer[]): Layer[] {
  const snapshotMap = new Map<string, Layer>();
  for (const layer of snapshotLayers) {
    snapshotMap.set(layer.id, layer);
  }

  return currentLayers.map((layer) => {
    if (layer.data.type !== 'image' || layer.data.src !== 'stripped:data-url-too-large') {
      return layer;
    }

    const snapshotLayer = snapshotMap.get(layer.id);
    if (!snapshotLayer || snapshotLayer.data.type !== 'image' || !snapshotLayer.data.src) {
      return layer;
    }

    // Don't restore if snapshot also has stripped marker
    if (snapshotLayer.data.src === 'stripped:data-url-too-large') {
      return layer;
    }

    return {
      ...layer,
      data: {
        ...layer.data,
        src: snapshotLayer.data.src,
      },
    };
  });
}

/**
 * Save editor state to sessionStorage
 */
function saveStateToSession(key: string, state: EditorState, productId: string): void {
  try {
    // Only persist design state (layers, tools, adjustments, canvas dimensions)
    // Don't persist transient state like loading/error
    // IMPORTANT: Strip large base64 data URLs to avoid quota issues (~5MB limit)
    const persistedState: EditorState = {
      layers: stripLargeDataUrls(state.layers),
      activeLayerId: state.activeLayerId,
      selectedLayerIds: state.selectedLayerIds,
      activeTool: state.activeTool,
      canvas: state.canvas,
      adjustments: state.adjustments,
      penSize: state.penSize,
      eraserSize: state.eraserSize,
      penColor: state.penColor,
      // Don't persist history - it can contain large data and will be rebuilt
      history: { past: [], future: [] },
    };

    sessionStorage.setItem(key, JSON.stringify(persistedState));
    sessionStorage.setItem(EDITOR_PRODUCT_ID_KEY, productId);
  } catch (error) {
    // If we still exceed quota (shouldn't happen after stripping), clear and retry
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      console.error(
        '[EditorContext] SessionStorage quota exceeded even after stripping — editor state will not be preserved across page refreshes'
      );
      try {
        sessionStorage.removeItem(key);
      } catch {
        // Ignore cleanup errors
      }
    } else {
      console.warn('[EditorContext] Failed to save state to session storage:', error);
    }
  }
}

/**
 * Clear editor state from sessionStorage
 */
function clearStateFromSession(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.warn('[EditorContext] Failed to clear state from session storage:', error);
  }
}

function createSnapshot(state: EditorState): EditorSnapshot {
  return {
    layers: JSON.parse(JSON.stringify(state.layers)),
    activeLayerId: state.activeLayerId,
    selectedLayerIds: [...state.selectedLayerIds],
    activeTool: state.activeTool,
    canvas: { ...state.canvas },
    adjustments: { ...state.adjustments },
    penSize: state.penSize,
    eraserSize: state.eraserSize,
    penColor: state.penColor,
  };
}

function applySnapshot(state: EditorState, snapshot: EditorSnapshot): EditorState {
  return {
    ...state,
    layers: JSON.parse(JSON.stringify(snapshot.layers)),
    activeLayerId: snapshot.activeLayerId,
    selectedLayerIds: snapshot.selectedLayerIds ? [...snapshot.selectedLayerIds] : [],
    activeTool: snapshot.activeTool,
    canvas: { ...snapshot.canvas },
    adjustments: { ...snapshot.adjustments },
    penSize: snapshot.penSize,
    eraserSize: snapshot.eraserSize,
    penColor: snapshot.penColor,
  };
}

function generateLayerId(): string {
  return `layer-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

// ============================================
// Reducer
// ============================================

function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'SET_TOOL':
      return { ...state, activeTool: action.payload };

    // NOTE: SET_ZOOM, SET_PAN, TOGGLE_GRID, TOGGLE_SNAP, RESET_VIEW actions removed
    // These now modify shared canvas state directly (handled in EditorProvider)

    case 'ADD_LAYER': {
      const snapshot = createSnapshot(state);
      // Insert user layers before the overlay layer (second-to-last position)
      // Layer order: [baseLayer, ...userLayers, overlayLayer]
      const overlayIndex = state.layers.findIndex((l) => l.id === 'product-overlay-layer');
      let newLayers: Layer[];
      if (overlayIndex > 0) {
        // Insert before overlay layer
        newLayers = [
          ...state.layers.slice(0, overlayIndex),
          action.payload,
          ...state.layers.slice(overlayIndex),
        ];
      } else {
        // No overlay layer found, just append
        newLayers = [...state.layers, action.payload];
      }
      return {
        ...state,
        layers: newLayers,
        activeLayerId: action.payload.id,
        history: {
          past: [...state.history.past, snapshot].slice(-50),
          future: [],
        },
      };
    }

    case 'ADD_LAYERS_BATCH': {
      const snapshot = createSnapshot(state);
      const overlayIndex = state.layers.findIndex((l) => l.id === 'product-overlay-layer');
      let newLayers: Layer[];
      if (overlayIndex > 0) {
        newLayers = [
          ...state.layers.slice(0, overlayIndex),
          ...action.payload,
          ...state.layers.slice(overlayIndex),
        ];
      } else {
        newLayers = [...state.layers, ...action.payload];
      }
      return {
        ...state,
        layers: newLayers,
        activeLayerId: action.payload.length > 0 ? action.payload[0]!.id : state.activeLayerId,
        history: {
          past: [...state.history.past, snapshot].slice(-50),
          future: [],
        },
      };
    }

    case 'DELETE_LAYER': {
      const snapshot = createSnapshot(state);
      const newLayers = state.layers.filter((l) => l.id !== action.payload);
      return {
        ...state,
        layers: newLayers,
        activeLayerId:
          state.activeLayerId === action.payload
            ? (newLayers[newLayers.length - 1]?.id ?? null)
            : state.activeLayerId,
        history: {
          past: [...state.history.past, snapshot].slice(-50),
          future: [],
        },
      };
    }

    case 'DUPLICATE_LAYER': {
      const layerToDuplicate = state.layers.find((l) => l.id === action.payload);
      if (!layerToDuplicate) return state;

      const snapshot = createSnapshot(state);
      const newLayer: Layer = {
        ...JSON.parse(JSON.stringify(layerToDuplicate)),
        id: generateLayerId(),
        name: `${layerToDuplicate.name} Copy`,
      };

      const index = state.layers.findIndex((l) => l.id === action.payload);
      const newLayers = [...state.layers];
      newLayers.splice(index + 1, 0, newLayer);

      return {
        ...state,
        layers: newLayers,
        activeLayerId: newLayer.id,
        history: {
          past: [...state.history.past, snapshot].slice(-50),
          future: [],
        },
      };
    }

    case 'REORDER_LAYERS': {
      const snapshot = createSnapshot(state);
      const reorderedLayers = action.payload
        .map((id) => state.layers.find((l) => l.id === id))
        .filter((l): l is Layer => l !== undefined);

      return {
        ...state,
        layers: reorderedLayers,
        history: {
          past: [...state.history.past, snapshot].slice(-50),
          future: [],
        },
      };
    }

    case 'UPDATE_LAYER': {
      const snapshot = createSnapshot(state);
      return {
        ...state,
        layers: state.layers.map((l) =>
          l.id === action.payload.id ? { ...l, ...action.payload.updates } : l
        ),
        history: {
          past: [...state.history.past, snapshot].slice(-50),
          future: [],
        },
      };
    }

    case 'ALIGN_LAYER_HORIZONTAL': {
      if (!state.activeLayerId) return state;
      const layer = state.layers.find((l) => l.id === state.activeLayerId);
      if (!layer || layer.locked) return state;

      const snapshot = createSnapshot(state);
      const alignment = action.payload;

      // Get layer dimensions
      let layerWidth = 0;
      if (layer.data.type === 'image') {
        layerWidth = layer.data.width;
      } else if (layer.data.type === 'text') {
        layerWidth = layer.data.width;
      }

      // Calculate new X position based on alignment
      let newX = 0;
      switch (alignment) {
        case 'left':
          newX = 0;
          break;
        case 'center':
          newX = (state.canvas.width - layerWidth) / 2;
          break;
        case 'right':
          newX = state.canvas.width - layerWidth;
          break;
      }

      return {
        ...state,
        layers: state.layers.map((l) =>
          l.id === state.activeLayerId ? { ...l, data: { ...l.data, x: newX } } : l
        ),
        history: {
          past: [...state.history.past, snapshot].slice(-50),
          future: [],
        },
      };
    }

    case 'ALIGN_LAYER_VERTICAL': {
      if (!state.activeLayerId) return state;
      const layer = state.layers.find((l) => l.id === state.activeLayerId);
      if (!layer || layer.locked) return state;

      const snapshot = createSnapshot(state);
      const alignment = action.payload;

      // Get layer dimensions
      let layerHeight = 0;
      if (layer.data.type === 'image') {
        layerHeight = layer.data.height;
      } else if (layer.data.type === 'text') {
        layerHeight = layer.data.height;
      }

      // Calculate new Y position based on alignment
      let newY = 0;
      switch (alignment) {
        case 'top':
          newY = 0;
          break;
        case 'middle':
          newY = (state.canvas.height - layerHeight) / 2;
          break;
        case 'bottom':
          newY = state.canvas.height - layerHeight;
          break;
      }

      return {
        ...state,
        layers: state.layers.map((l) =>
          l.id === state.activeLayerId ? { ...l, data: { ...l.data, y: newY } } : l
        ),
        history: {
          past: [...state.history.past, snapshot].slice(-50),
          future: [],
        },
      };
    }

    case 'DISTRIBUTE_LAYERS': {
      // Need at least 3 selected layers to distribute
      if (state.selectedLayerIds.length < 3) return state;

      const direction = action.payload;
      const selectedLayers = state.layers.filter(
        (l) => state.selectedLayerIds.includes(l.id) && !l.locked
      );

      if (selectedLayers.length < 3) return state;

      const snapshot = createSnapshot(state);

      // Get position and size info for each layer
      const layerInfo = selectedLayers.map((layer) => {
        let x = 0,
          y = 0,
          width = 0,
          height = 0;
        if (layer.data.type === 'image' || layer.data.type === 'shape') {
          x = layer.data.x;
          y = layer.data.y;
          width = layer.data.width;
          height = layer.data.height;
        } else if (layer.data.type === 'text') {
          x = layer.data.x;
          y = layer.data.y;
          width = layer.data.width;
          height = layer.data.height;
        }
        return { id: layer.id, x, y, width, height };
      });

      // Sort by position (left-to-right for horizontal, top-to-bottom for vertical)
      if (direction === 'horizontal') {
        layerInfo.sort((a, b) => a.x - b.x);
      } else {
        layerInfo.sort((a, b) => a.y - b.y);
      }

      // Calculate total span and even spacing
      const first = layerInfo[0];
      const last = layerInfo[layerInfo.length - 1];
      if (!first || !last) return state;

      if (direction === 'horizontal') {
        // Total span from left edge of first to right edge of last
        const totalSpan = last.x + last.width - first.x;
        // Total width of all layers
        const totalWidth = layerInfo.reduce((sum, l) => sum + l.width, 0);
        // Total gap to distribute among n-1 spaces
        const totalGap = totalSpan - totalWidth;
        const gapSize = totalGap / (layerInfo.length - 1);

        // Calculate new positions (keeping first and last in place)
        let currentX = first.x + first.width + gapSize;
        for (let i = 1; i < layerInfo.length - 1; i++) {
          const info = layerInfo[i];
          if (info) {
            info.x = currentX;
            currentX += info.width + gapSize;
          }
        }
      } else {
        // Vertical distribution
        const totalSpan = last.y + last.height - first.y;
        const totalHeight = layerInfo.reduce((sum, l) => sum + l.height, 0);
        const totalGap = totalSpan - totalHeight;
        const gapSize = totalGap / (layerInfo.length - 1);

        let currentY = first.y + first.height + gapSize;
        for (let i = 1; i < layerInfo.length - 1; i++) {
          const info = layerInfo[i];
          if (info) {
            info.y = currentY;
            currentY += info.height + gapSize;
          }
        }
      }

      // Create new positions map
      const newPositions = new Map(layerInfo.map((info) => [info.id, { x: info.x, y: info.y }]));

      return {
        ...state,
        layers: state.layers.map((l) => {
          const newPos = newPositions.get(l.id);
          if (!newPos) return l;
          return {
            ...l,
            data: { ...l.data, x: newPos.x, y: newPos.y },
          };
        }),
        history: {
          past: [...state.history.past, snapshot].slice(-50),
          future: [],
        },
      };
    }

    case 'SET_ACTIVE_LAYER':
      // Don't allow selecting product layers
      if (action.payload && isProductLayer(action.payload)) return state;
      return {
        ...state,
        activeLayerId: action.payload,
        // When setting active layer, also update selectedLayerIds to include just this layer
        selectedLayerIds: action.payload ? [action.payload] : [],
      };

    case 'SELECT_LAYER': {
      const { id, addToSelection } = action.payload;
      const layer = state.layers.find((l) => l.id === id);
      // Don't allow selecting locked or product layers
      if (!layer || layer.locked || isProductLayer(id)) return state;

      if (addToSelection) {
        // Shift+click behavior: toggle layer in selection
        const isAlreadySelected = state.selectedLayerIds.includes(id);
        if (isAlreadySelected) {
          // Remove from selection
          const newSelectedIds = state.selectedLayerIds.filter((lid) => lid !== id);
          return {
            ...state,
            selectedLayerIds: newSelectedIds,
            // Update activeLayerId to be the last selected or null
            activeLayerId:
              newSelectedIds.length > 0
                ? (newSelectedIds[newSelectedIds.length - 1] ?? null)
                : null,
          };
        } else {
          // Add to selection
          return {
            ...state,
            selectedLayerIds: [...state.selectedLayerIds, id],
            activeLayerId: id,
          };
        }
      } else {
        // Regular click: select only this layer
        return {
          ...state,
          selectedLayerIds: [id],
          activeLayerId: id,
        };
      }
    }

    case 'SELECT_ALL_LAYERS': {
      // Select all user layers (not product layers)
      const userLayerIds = state.layers
        .filter(
          (l) =>
            !l.locked &&
            l.id !== 'product-base-layer' &&
            l.id !== 'product-overlay-layer' &&
            l.id !== 'product-dark-overlay' &&
            l.id !== 'product-production-frame'
        )
        .map((l) => l.id);
      return {
        ...state,
        selectedLayerIds: userLayerIds,
        activeLayerId:
          userLayerIds.length > 0 ? (userLayerIds[userLayerIds.length - 1] ?? null) : null,
      };
    }

    case 'CLEAR_SELECTION':
      return {
        ...state,
        selectedLayerIds: [],
        activeLayerId: null,
      };

    case 'SET_ADJUSTMENT': {
      const snapshot = createSnapshot(state);
      return {
        ...state,
        adjustments: { ...state.adjustments, ...action.payload },
        history: {
          past: [...state.history.past, snapshot].slice(-50),
          future: [],
        },
      };
    }

    case 'SET_PEN_SIZE':
      return { ...state, penSize: Math.max(1, Math.min(100, action.payload)) };

    case 'SET_ERASER_SIZE':
      return { ...state, eraserSize: Math.max(1, Math.min(100, action.payload)) };

    case 'SET_PEN_COLOR':
      return { ...state, penColor: action.payload };

    case 'UNDO': {
      const previousSnapshot = state.history.past[state.history.past.length - 1];
      if (!previousSnapshot) return state;

      const currentSnapshot = createSnapshot(state);

      return {
        ...applySnapshot(state, previousSnapshot),
        history: {
          past: state.history.past.slice(0, -1),
          future: [currentSnapshot, ...state.history.future].slice(0, 50),
        },
      };
    }

    case 'REDO': {
      const nextSnapshot = state.history.future[0];
      if (!nextSnapshot) return state;

      const currentSnapshot = createSnapshot(state);

      return {
        ...applySnapshot(state, nextSnapshot),
        history: {
          past: [...state.history.past, currentSnapshot].slice(-50),
          future: state.history.future.slice(1),
        },
      };
    }

    case 'SAVE_SNAPSHOT': {
      const snapshot = createSnapshot(state);
      return {
        ...state,
        history: {
          past: [...state.history.past, snapshot].slice(-50),
          future: [],
        },
      };
    }

    case 'INIT_PRODUCT_LAYERS': {
      const {
        baseLayer,
        overlayLayer,
        darkOverlayLayer,
        productionFrameLayer,
        canvasWidth,
        canvasHeight,
      } = action.payload;
      // Layer order: [base, overlay, darkOverlay, productionFrame]
      // User layers will be inserted before overlay (see ADD_LAYER)
      // Final order: [base, ...userLayers, overlay, darkOverlay, productionFrame]
      return {
        ...state,
        layers: [baseLayer, overlayLayer, darkOverlayLayer, productionFrameLayer],
        activeLayerId: null,
        selectedLayerIds: [],
        canvas: {
          ...state.canvas,
          width: canvasWidth,
          height: canvasHeight,
        },
        history: {
          past: [],
          future: [],
        },
      };
    }

    case 'CLEAR_USER_LAYERS': {
      const snapshot = createSnapshot(state);
      // Keep only product layers (base, overlay, dark overlay, production frame)
      const productLayers = state.layers.filter(
        (l) =>
          l.id === 'product-base-layer' ||
          l.id === 'product-overlay-layer' ||
          l.id === 'product-dark-overlay' ||
          l.id === 'product-production-frame'
      );
      return {
        ...state,
        layers: productLayers,
        activeLayerId: null,
        selectedLayerIds: [],
        history: {
          past: [...state.history.past, snapshot].slice(-50),
          future: [],
        },
      };
    }

    case 'UPDATE_BASE_LAYER_IMAGE': {
      // Update the base layer image src AND dimensions (used by AI mode to change preview)
      // Recalculate centered position based on new dimensions
      // No history update - this is a visual change, not a user edit
      const { src, width, height } = action.payload;
      const centeredX = (state.canvas.width - width) / 2;
      const centeredY = (state.canvas.height - height) / 2;

      return {
        ...state,
        layers: state.layers.map((l) => {
          if (l.id === 'product-base-layer' && l.data.type === 'image') {
            return {
              ...l,
              data: {
                ...l.data,
                src,
                width,
                height,
                x: centeredX,
                y: centeredY,
              },
            };
          }
          return l;
        }),
      };
    }

    case 'LOAD_DESIGN_LAYERS': {
      const snapshot = createSnapshot(state);
      const {
        layers: designLayers,
        activeLayerId,
        activeTool,
        adjustments,
        penSize,
        eraserSize,
        penColor,
      } = action.payload;

      // Preserve current product layers (base, overlay, dark overlay, production frame)
      const currentBaseLayer = state.layers.find((l) => l.id === 'product-base-layer');
      const currentOverlayLayer = state.layers.find((l) => l.id === 'product-overlay-layer');
      const currentDarkOverlay = state.layers.find((l) => l.id === 'product-dark-overlay');
      const currentProductionFrame = state.layers.find((l) => l.id === 'product-production-frame');

      // Extract user layers from the design (non-product layers)
      const userLayers = designLayers.filter(
        (l) =>
          l.id !== 'product-base-layer' &&
          l.id !== 'product-overlay-layer' &&
          l.id !== 'product-dark-overlay' &&
          l.id !== 'product-production-frame'
      );

      // Build merged layers: [base, ...userLayers, overlay, darkOverlay, productionFrame]
      // Prefer snapshot's product layers over current state — the snapshot was saved
      // with the correct product, while current state may have stale layers from
      // a different product during a product switch (before INIT_PRODUCT_LAYERS fires).
      // INIT_PRODUCT_LAYERS will replace these with the correct new product layers later.
      const snapshotBase = designLayers.find((l) => l.id === 'product-base-layer');
      const snapshotOverlay = designLayers.find((l) => l.id === 'product-overlay-layer');
      const snapshotDark = designLayers.find((l) => l.id === 'product-dark-overlay');
      const snapshotProdFrame = designLayers.find((l) => l.id === 'product-production-frame');

      const baseLayer = snapshotBase || currentBaseLayer;
      const overlayLayer = snapshotOverlay || currentOverlayLayer;
      const darkOverlay = snapshotDark || currentDarkOverlay;
      const productionFrame = snapshotProdFrame || currentProductionFrame;

      const mergedLayers: Layer[] = [];
      if (baseLayer) mergedLayers.push(baseLayer);
      mergedLayers.push(...JSON.parse(JSON.stringify(userLayers)));
      if (overlayLayer) mergedLayers.push(overlayLayer);
      if (darkOverlay) mergedLayers.push(darkOverlay);
      // Always force production frame hidden during editing (opacity 0)
      // Snapshots may have saved it with opacity > 0 from an export
      if (productionFrame) mergedLayers.push({ ...productionFrame, opacity: 0 });

      return {
        ...state,
        layers: mergedLayers,
        activeLayerId,
        activeTool,
        canvas: {
          ...state.canvas,
          // Keep current width/height from product
        },
        adjustments: { ...adjustments },
        penSize,
        eraserSize,
        penColor,
        history: {
          past: [...state.history.past, snapshot].slice(-50),
          future: [],
        },
      };
    }

    case 'RESTORE_IMAGES': {
      // Restore stripped image data without creating undo history
      // This is automatic restoration from IndexedDB, not a user action
      return {
        ...state,
        layers: action.payload.layers,
      };
    }

    default:
      return state;
  }
}

// ============================================
// Context
// ============================================

interface EditorContextValue {
  state: EditorState;
  dispatch: React.Dispatch<EditorAction>;
  // Convenience methods
  setTool: (tool: ManualTool) => void;
  setZoom: (zoom: number) => void;
  setPan: (x: number, y: number) => void;
  toggleGrid: () => void;
  toggleSnap: () => void;
  toggleGuides: () => void;
  resetView: () => void;
  addLayer: (layer: Layer) => void;
  addLayersBatch: (layers: Layer[]) => void;
  deleteLayer: (id: string) => void;
  duplicateLayer: (id: string) => void;
  reorderLayers: (ids: string[]) => void;
  updateLayer: (id: string, updates: Partial<Layer>) => void;
  setActiveLayer: (id: string | null) => void;
  selectLayer: (id: string, addToSelection: boolean) => void;
  selectAllLayers: () => void;
  clearSelection: () => void;
  setAdjustment: (adjustment: Partial<AdjustmentState>) => void;
  setPenSize: (size: number) => void;
  setEraserSize: (size: number) => void;
  setPenColor: (color: 'black' | 'white') => void;
  // Alignment methods
  alignLayerHorizontal: (alignment: HorizontalAlignment) => void;
  alignLayerVertical: (alignment: VerticalAlignment) => void;
  distributeLayers: (direction: DistributeDirection) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  getSnapshot: () => EditorSnapshot;
  // Auto-save
  saveStatus: SaveStatus;
  saveNow: () => Promise<void>;
  lastSaved: Date | null;
  // Library integration
  saveToLibrary: () => Promise<string | null>;
  loadFromLibrary: (designId: string) => Promise<void>;
  loadSnapshot: (snapshot: EditorSnapshot) => Promise<void>;
  clearUserLayers: () => void;
  hasUserLayers: () => boolean;
  updateBaseLayerImage: (src: string, width: number, height: number) => void;
  // Orientation
  orientation: Orientation;
  setOrientation: (orientation: Orientation) => void;
  // Thumbnail generation (registered by ManualPreview)
  registerThumbnailGenerator: (generator: (() => Promise<string | null>) | null) => void;
  generateThumbnail: () => Promise<string | null>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const EditorContext = createContext<EditorContextValue | null>(null);

// ============================================
// Provider
// ============================================

interface EditorProviderProps {
  children: ReactNode;
  /** The selected product - used to initialize locked base/overlay layers */
  product: Product;
  /** The active side (front or back) - determines which state to expose */
  activeSide: Side;
}

/** Reserved layer IDs for product layers */
const PRODUCT_BASE_LAYER_ID = 'product-base-layer';
const PRODUCT_OVERLAY_LAYER_ID = 'product-overlay-layer';
const PRODUCT_DARK_OVERLAY_ID = 'product-dark-overlay';
const PRODUCT_PRODUCTION_FRAME_ID = 'product-production-frame';

/** Check if a layer ID belongs to a system product layer */
function isProductLayer(layerId: string): boolean {
  return (
    layerId === PRODUCT_BASE_LAYER_ID ||
    layerId === PRODUCT_OVERLAY_LAYER_ID ||
    layerId === PRODUCT_DARK_OVERLAY_ID ||
    layerId === PRODUCT_PRODUCTION_FRAME_ID
  );
}

// CANVAS_WIDTH (1024) and CANVAS_HEIGHT (1536) imported from @/utils/canvasConstants

/**
 * Creates product layers from a Product for a specific side
 * - Base layer: product preview image (locked, 100% opacity, centered at native size)
 * - Overlay layer: frame overlay (locked, 15% opacity, fills canvas)
 * - Production frame layer: production export frame (locked, hidden, fills canvas)
 *
 * Uses product-specific canvas dimensions when available (e.g., wallet = 1024x1664)
 */
function createProductLayers(
  product: Product,
  side: Side,
  baseImageWidth: number,
  baseImageHeight: number,
  orientation: Orientation = 'portrait'
): ProductLayersPayload {
  const baseImage = resolveProductImage(product.id, null, orientation);
  const frameOverlay = side === 'front' ? product.frontFrameOverlay : product.backFrameOverlay;
  const productionFrame =
    side === 'front' ? product.frontProductionFrame : product.backProductionFrame;

  // Use oriented canvas dimensions (swap width/height for landscape)
  const portraitCw = product.canvasWidth ?? CANVAS_WIDTH;
  const portraitCh = product.canvasHeight ?? CANVAS_HEIGHT;
  const cw = orientation === 'landscape' ? portraitCh : portraitCw;
  const ch = orientation === 'landscape' ? portraitCw : portraitCh;

  // Base image dimensions match the loaded image (already oriented via landscape/ subfolder)
  const bw = baseImageWidth;
  const bh = baseImageHeight;

  // Center the base image within the oriented canvas
  const baseX = (cw - bw) / 2;
  const baseY = (ch - bh) / 2;

  // Helper to get landscape path for frame overlays
  const getFramePath = (path: string | undefined): string | undefined => {
    if (!path || orientation === 'portrait') return path;
    const lastSlash = path.lastIndexOf('/');
    return path.slice(0, lastSlash) + '/landscape' + path.slice(lastSlash);
  };

  const frameOverlayPath = getFramePath(frameOverlay);
  const productionFramePath = getFramePath(productionFrame);

  const baseLayer: Layer = {
    id: PRODUCT_BASE_LAYER_ID,
    name: 'Product Base',
    type: 'image',
    visible: true,
    locked: true,
    inverted: false,
    opacity: 1,
    data: {
      type: 'image',
      src: baseImage,
      x: baseX,
      y: baseY,
      width: bw,
      height: bh,
      rotation: 0,
      flipH: false,
      flipV: false,
    },
  };

  const overlayLayer: Layer = {
    id: PRODUCT_OVERLAY_LAYER_ID,
    name: 'Product Frame',
    type: 'image',
    visible: true,
    locked: true,
    inverted: false,
    opacity: 0.95,
    data: {
      type: 'image',
      src: frameOverlayPath ?? baseImage,
      x: 0,
      y: 0,
      width: cw,
      height: ch,
      rotation: 0,
      flipH: false,
      flipV: false,
    },
  };

  // Dark overlay layer - shows on top of user layers at 80% opacity
  // Uses the dark frame PNG to improve visual appearance
  const darkOverlayLayer: Layer = {
    id: PRODUCT_DARK_OVERLAY_ID,
    name: 'Dark Frame',
    type: 'image',
    visible: true,
    locked: true,
    inverted: false,
    opacity: 0.8, // 80% visible as requested
    data: {
      type: 'image',
      src: frameOverlayPath ?? baseImage,
      x: 0,
      y: 0,
      width: cw,
      height: ch,
      rotation: 0,
      flipH: false,
      flipV: false,
    },
  };

  // Production frame layer - hidden during editing, shown only during production export
  // NOTE: We use opacity: 0 instead of visible: false so the image is preloaded
  const productionFrameLayer: Layer = {
    id: PRODUCT_PRODUCTION_FRAME_ID,
    name: 'Production Frame',
    type: 'image',
    visible: true, // Always render so image is preloaded
    locked: true,
    inverted: false,
    opacity: 0, // Hidden via opacity - set to 1 during export
    data: {
      type: 'image',
      src: productionFramePath ?? frameOverlayPath ?? baseImage,
      x: 0,
      y: 0,
      width: cw,
      height: ch,
      rotation: 0,
      flipH: false,
      flipV: false,
    },
  };

  return {
    baseLayer,
    overlayLayer,
    darkOverlayLayer,
    productionFrameLayer,
    canvasWidth: cw,
    canvasHeight: ch,
  };
}

export function EditorProvider({ children, product, activeSide }: EditorProviderProps) {
  // Maintain TWO complete reducer states internally with sessionStorage persistence
  // This ensures canvas work is preserved during auth flows
  const [frontState, frontDispatch] = useReducer(
    editorReducer,
    initialState,
    () => loadStateFromSession(EDITOR_STATE_FRONT_KEY, product.id) || initialState
  );
  const [backState, backDispatch] = useReducer(
    editorReducer,
    initialState,
    () => loadStateFromSession(EDITOR_STATE_BACK_KEY, product.id) || initialState
  );

  // Shared canvas state (zoom, pan, grid) - applies to both front and back
  const [sharedCanvasState, setSharedCanvasState] = useState(initialSharedCanvasState);

  // Orientation state - persisted in sessionStorage
  const [orientation, setOrientationState] = useState<Orientation>(() => {
    try {
      const saved = sessionStorage.getItem(EDITOR_ORIENTATION_KEY);
      if (saved === 'landscape') return 'landscape';
    } catch {
      /* ignore */
    }
    return 'portrait';
  });

  // Persist front state to sessionStorage whenever it changes
  useEffect(() => {
    saveStateToSession(EDITOR_STATE_FRONT_KEY, frontState, product.id);
  }, [frontState, product.id]);

  // Persist back state to sessionStorage whenever it changes
  useEffect(() => {
    saveStateToSession(EDITOR_STATE_BACK_KEY, backState, product.id);
  }, [backState, product.id]);

  // Persist orientation to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem(EDITOR_ORIENTATION_KEY, orientation);
    } catch {
      /* ignore */
    }
  }, [orientation]);

  // Track which sides have been initialized for the current product
  const initializedProductRef = useRef<{ productId: string | null; front: boolean; back: boolean }>(
    {
      productId: null,
      front: false,
      back: false,
    }
  );

  // Refs to track latest state for async checks (avoids closure issues)
  const frontStateRef = useRef(frontState);
  const backStateRef = useRef(backState);

  // Thumbnail generator function - registered by ManualPreview
  const thumbnailGeneratorRef = useRef<(() => Promise<string | null>) | null>(null);

  // Keep refs in sync with state
  useEffect(() => {
    frontStateRef.current = frontState;
  }, [frontState]);

  useEffect(() => {
    backStateRef.current = backState;
  }, [backState]);

  // Get the active side's state (without canvas - we'll merge it)
  const activeState = activeSide === 'front' ? frontState : backState;
  const dispatch = activeSide === 'front' ? frontDispatch : backDispatch;

  // Merge shared canvas state with per-side canvas state (width/height)
  // Wrapped in useMemo to avoid creating new object on every render
  const state: EditorState = useMemo(
    () => ({
      ...activeState,
      canvas: {
        ...sharedCanvasState,
        width: activeState.canvas.width,
        height: activeState.canvas.height,
      },
    }),
    [activeState, sharedCanvasState]
  );

  // Initialize product layers for front side when product changes
  useEffect(() => {
    if (!product) return;

    const needsReset = initializedProductRef.current.productId !== product.id;

    if (needsReset) {
      // New product - reset tracking
      initializedProductRef.current = {
        productId: product.id,
        front: false,
        back: false,
      };
    }

    // Initialize front side if not already initialized
    if (!initializedProductRef.current.front) {
      // Check if sessionStorage already restored valid product layers for this product
      const hasExistingProductLayers = frontStateRef.current.layers.some(
        (l) => l.id === PRODUCT_BASE_LAYER_ID
      );
      if (hasExistingProductLayers && !needsReset) {
        // SessionStorage restored valid state — preserve user layers
        initializedProductRef.current.front = true;
      } else {
        const img = new Image();
        img.onload = () => {
          const payload = createProductLayers(product, 'front', img.width, img.height, orientation);
          frontDispatch({ type: 'INIT_PRODUCT_LAYERS', payload });
          initializedProductRef.current.front = true;
        };
        img.onerror = () => {
          console.error(`Failed to load product front image for: ${product.id}`);
        };
        img.src = resolveProductImage(product.id, null, orientation);
      }
    }
  }, [product, orientation]);

  // Lazy initialization: Initialize back side only when first accessed
  useEffect(() => {
    if (!product || activeSide !== 'back') return;

    // Initialize back side if not already initialized
    if (!initializedProductRef.current.back) {
      // Check if sessionStorage already restored valid product layers for this product
      const hasExistingProductLayers = backStateRef.current.layers.some(
        (l) => l.id === PRODUCT_BASE_LAYER_ID
      );
      if (hasExistingProductLayers) {
        // SessionStorage restored valid state — preserve user layers
        initializedProductRef.current.back = true;
      } else {
        const img = new Image();
        img.onload = () => {
          const payload = createProductLayers(product, 'back', img.width, img.height, orientation);
          backDispatch({ type: 'INIT_PRODUCT_LAYERS', payload });
          initializedProductRef.current.back = true;
        };
        img.onerror = () => {
          console.error(`Failed to load product back image for: ${product.id}`);
        };
        img.src = resolveProductImage(product.id, null, orientation);
      }
    }
  }, [product, activeSide, orientation]);

  // Reset shared canvas state (zoom/pan) when product changes
  useEffect(() => {
    if (!product) return;

    const needsReset = initializedProductRef.current.productId !== product.id;
    if (needsReset) {
      // New product - reset zoom/pan to initial state
      setSharedCanvasState(initialSharedCanvasState);

      // Clear sessionStorage for old product (new product's state will be loaded by lazy init)
      clearStateFromSession(EDITOR_STATE_FRONT_KEY);
      clearStateFromSession(EDITOR_STATE_BACK_KEY);
      sessionStorage.removeItem(EDITOR_PRODUCT_ID_KEY);
    }
  }, [product]);

  // Restore stripped images from IndexedDB auto-save on mount
  // When sessionStorage has 'stripped:data-url-too-large' markers (after page refresh),
  // the in-memory cache is gone, so we fall back to the latest IndexedDB auto-save snapshot.
  const imageRestorationAttemptedRef = useRef(false);
  useEffect(() => {
    if (imageRestorationAttemptedRef.current) return;
    imageRestorationAttemptedRef.current = true;

    const restoreImages = async () => {
      const userId = auth?.user?.id ?? 'anonymous';
      const sidesToRestore: Array<{
        side: 'front' | 'back';
        layers: Layer[];
        dispatch: typeof frontDispatch;
      }> = [];

      if (hasStrippedImages(frontState.layers)) {
        sidesToRestore.push({ side: 'front', layers: frontState.layers, dispatch: frontDispatch });
      }
      if (hasStrippedImages(backState.layers)) {
        sidesToRestore.push({ side: 'back', layers: backState.layers, dispatch: backDispatch });
      }

      if (sidesToRestore.length === 0) return;

      for (const { side, layers, dispatch: sideDispatch } of sidesToRestore) {
        try {
          const autoSave = await getLatestAutoSave(userId, product.id, side);
          if (!autoSave) {
            console.warn(`[EditorContext] No auto-save found for ${side} side image restoration`);
            continue;
          }

          const restoredLayers = restoreImagesFromSnapshot(layers, autoSave.snapshot.layers);

          // Count how many images were actually restored
          const restoredCount = restoredLayers.filter(
            (layer, i) =>
              layer.data.type === 'image' &&
              layers[i]?.data.type === 'image' &&
              (layers[i].data as { src: string }).src === 'stripped:data-url-too-large' &&
              layer.data.src !== 'stripped:data-url-too-large'
          ).length;

          if (restoredCount > 0) {
            sideDispatch({ type: 'RESTORE_IMAGES', payload: { side, layers: restoredLayers } });
            console.warn(
              `[EditorContext] Restored ${restoredCount} image(s) from auto-save for ${side} side`
            );
          }
        } catch (err) {
          console.error(`[EditorContext] Failed to restore images for ${side} side:`, err);
        }
      }
    };

    restoreImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get current snapshot for auto-save
  const getSnapshot = useCallback((): EditorSnapshot => {
    return createSnapshot(state);
  }, [state]);

  // Auto-save integration — skip saving when layers are empty (uninitialized back side)
  const auth = useAuthSafe();
  const currentSnapshot = getSnapshot();
  const autoSaveSnapshot = currentSnapshot.layers.length > 0 ? currentSnapshot : null;
  const autoSave = useAutoSave({
    userId: auth?.user?.id ?? null,
    productId: product.id,
    side: activeSide,
    snapshot: autoSaveSnapshot,
    debounceMs: 10000, // 10 seconds
    enabled: true,
  });

  // Flush pending auto-save when switching sides so the outgoing side's edits are persisted
  const prevSideRef = useRef<Side>(activeSide);
  useEffect(() => {
    if (prevSideRef.current !== activeSide) {
      // Refs still hold the previous side's values at this point, so saveNow() captures them
      autoSave.saveNow();
      prevSideRef.current = activeSide;
    }
  }, [activeSide, autoSave]);

  // Initial sync on mount (download cloud auto-saves)
  useSyncOnLoad({
    userId: auth?.user?.id ?? null,
    productId: product.id,
    side: activeSide,
    enabled: true,
  });

  const setTool = useCallback(
    (tool: ManualTool) => {
      dispatch({ type: 'SET_TOOL', payload: tool });
    },
    [dispatch]
  );

  const setZoom = useCallback((zoom: number) => {
    const clampedZoom = Math.max(25, Math.min(400, zoom));
    setSharedCanvasState((prev) => ({ ...prev, zoom: clampedZoom }));
  }, []);

  const setPan = useCallback((x: number, y: number) => {
    setSharedCanvasState((prev) => ({ ...prev, panX: x, panY: y }));
  }, []);

  const toggleGrid = useCallback(() => {
    setSharedCanvasState((prev) => ({ ...prev, showGrid: !prev.showGrid }));
  }, []);

  const toggleSnap = useCallback(() => {
    setSharedCanvasState((prev) => ({ ...prev, snapToGrid: !prev.snapToGrid }));
  }, []);

  const toggleGuides = useCallback(() => {
    setSharedCanvasState((prev) => ({ ...prev, showGuides: !prev.showGuides }));
  }, []);

  const resetView = useCallback(() => {
    setSharedCanvasState(initialSharedCanvasState);
  }, []);

  const addLayer = useCallback(
    (layer: Layer) => {
      dispatch({ type: 'ADD_LAYER', payload: layer });
    },
    [dispatch]
  );

  const addLayersBatch = useCallback(
    (layers: Layer[]) => {
      if (layers.length > 0) {
        dispatch({ type: 'ADD_LAYERS_BATCH', payload: layers });
      }
    },
    [dispatch]
  );

  const deleteLayer = useCallback(
    (id: string) => {
      dispatch({ type: 'DELETE_LAYER', payload: id });
    },
    [dispatch]
  );

  const duplicateLayer = useCallback(
    (id: string) => {
      dispatch({ type: 'DUPLICATE_LAYER', payload: id });
    },
    [dispatch]
  );

  const reorderLayers = useCallback(
    (ids: string[]) => {
      dispatch({ type: 'REORDER_LAYERS', payload: ids });
    },
    [dispatch]
  );

  const updateLayer = useCallback(
    (id: string, updates: Partial<Layer>) => {
      dispatch({ type: 'UPDATE_LAYER', payload: { id, updates } });
    },
    [dispatch]
  );

  const setActiveLayer = useCallback(
    (id: string | null) => {
      dispatch({ type: 'SET_ACTIVE_LAYER', payload: id });
    },
    [dispatch]
  );

  const selectLayer = useCallback(
    (id: string, addToSelection: boolean) => {
      dispatch({ type: 'SELECT_LAYER', payload: { id, addToSelection } });
    },
    [dispatch]
  );

  const selectAllLayers = useCallback(() => {
    dispatch({ type: 'SELECT_ALL_LAYERS' });
  }, [dispatch]);

  const clearSelection = useCallback(() => {
    dispatch({ type: 'CLEAR_SELECTION' });
  }, [dispatch]);

  const setAdjustment = useCallback(
    (adjustment: Partial<AdjustmentState>) => {
      dispatch({ type: 'SET_ADJUSTMENT', payload: adjustment });
    },
    [dispatch]
  );

  const setPenSize = useCallback(
    (size: number) => {
      dispatch({ type: 'SET_PEN_SIZE', payload: size });
    },
    [dispatch]
  );

  const setEraserSize = useCallback(
    (size: number) => {
      dispatch({ type: 'SET_ERASER_SIZE', payload: size });
    },
    [dispatch]
  );

  const setPenColor = useCallback(
    (color: 'black' | 'white') => {
      dispatch({ type: 'SET_PEN_COLOR', payload: color });
    },
    [dispatch]
  );

  const alignLayerHorizontal = useCallback(
    (alignment: HorizontalAlignment) => {
      dispatch({ type: 'ALIGN_LAYER_HORIZONTAL', payload: alignment });
    },
    [dispatch]
  );

  const alignLayerVertical = useCallback(
    (alignment: VerticalAlignment) => {
      dispatch({ type: 'ALIGN_LAYER_VERTICAL', payload: alignment });
    },
    [dispatch]
  );

  const distributeLayers = useCallback(
    (direction: DistributeDirection) => {
      dispatch({ type: 'DISTRIBUTE_LAYERS', payload: direction });
    },
    [dispatch]
  );

  const undo = useCallback(() => {
    dispatch({ type: 'UNDO' });
  }, [dispatch]);

  const redo = useCallback(() => {
    dispatch({ type: 'REDO' });
  }, [dispatch]);

  // Library integration methods
  const clearUserLayers = useCallback(() => {
    dispatch({ type: 'CLEAR_USER_LAYERS' });
  }, [dispatch]);

  const hasUserLayers = useCallback(() => {
    // Check if there are any layers other than product layers (base, overlay, dark overlay, production frame)
    return state.layers.some(
      (l) =>
        l.id !== 'product-base-layer' &&
        l.id !== 'product-overlay-layer' &&
        l.id !== 'product-dark-overlay' &&
        l.id !== 'product-production-frame'
    );
  }, [state.layers]);

  const updateBaseLayerImage = useCallback(
    (src: string, width: number, height: number) => {
      dispatch({ type: 'UPDATE_BASE_LAYER_IMAGE', payload: { src, width, height } });
    },
    [dispatch]
  );

  const setOrientation = useCallback(
    (newOrientation: Orientation) => {
      if (newOrientation === orientation) return;

      setOrientationState(newOrientation);

      // Reset zoom/pan
      setSharedCanvasState(initialSharedCanvasState);

      // Clear user layers on both sides
      frontDispatch({ type: 'CLEAR_USER_LAYERS' });
      backDispatch({ type: 'CLEAR_USER_LAYERS' });

      // Force re-initialization of product layers
      initializedProductRef.current = {
        productId: product.id,
        front: false,
        back: false,
      };

      // Re-initialize front side with new orientation
      const img = new Image();
      img.onload = () => {
        const payload = createProductLayers(
          product,
          'front',
          img.width,
          img.height,
          newOrientation
        );
        frontDispatch({ type: 'INIT_PRODUCT_LAYERS', payload });
        initializedProductRef.current.front = true;
      };
      img.src = resolveProductImage(product.id, null, newOrientation);

      // Re-initialize back side if it was previously initialized
      if (activeSide === 'back') {
        const backImg = new Image();
        backImg.onload = () => {
          const payload = createProductLayers(
            product,
            'back',
            backImg.width,
            backImg.height,
            newOrientation
          );
          backDispatch({ type: 'INIT_PRODUCT_LAYERS', payload });
          initializedProductRef.current.back = true;
        };
        backImg.src = resolveProductImage(product.id, null, newOrientation);
      }
    },
    [orientation, product, activeSide]
  );

  // Register thumbnail generator (called by ManualPreview)
  const registerThumbnailGenerator = useCallback(
    (generator: (() => Promise<string | null>) | null) => {
      thumbnailGeneratorRef.current = generator;
    },
    []
  );

  // Generate thumbnail using registered generator
  const generateThumbnail = useCallback(async (): Promise<string | null> => {
    if (!thumbnailGeneratorRef.current) {
      console.warn('[EditorContext] No thumbnail generator registered');
      return null;
    }
    return thumbnailGeneratorRef.current();
  }, []);

  const loadFromLibrary = useCallback(
    async (designId: string) => {
      // Wait for product layers to ACTUALLY be in state before loading design
      // This prevents race condition where design loads before product base/overlay
      // We check the actual state via refs (not just the initialization flag)
      const hasProductLayersInState = () => {
        const stateRef = activeSide === 'front' ? frontStateRef : backStateRef;
        return stateRef.current.layers.some((l) => l.id === PRODUCT_BASE_LAYER_ID);
      };

      // If product layers not yet in state, wait for them (max 5 seconds)
      if (!hasProductLayersInState()) {
        console.warn('[EditorContext] Waiting for product layers in state...');
        const startTime = Date.now();
        const timeout = 5000; // 5 second timeout

        await new Promise<void>((resolve) => {
          const checkInterval = setInterval(() => {
            if (hasProductLayersInState() || Date.now() - startTime > timeout) {
              clearInterval(checkInterval);
              resolve();
            }
          }, 50); // Check every 50ms
        });

        if (!hasProductLayersInState()) {
          console.error('[EditorContext] Timeout waiting for product layers in state');
          throw new Error('Product layers not initialized');
        }
        console.warn('[EditorContext] Product layers found in state, proceeding with design load');
      }

      const { getDesigns } = await import('@/services/storage');
      const designs = await getDesigns();
      const design = designs.find((d) => d.id === designId);

      if (!design) {
        throw new Error(`Design ${designId} not found`);
      }

      // Always prefer creating fresh blob URLs from stored blobs rather than using
      // stored layers, which may contain stale blob URLs from previous sessions.
      // Blob URLs are session-specific and become invalid after page refresh.

      // Try current side's blob first, then fallback to other side
      let blob = activeSide === 'front' ? design.frontBlob : design.backBlob;
      let loadedFromOtherSide = false;

      if (!blob) {
        // Try the other side's blob
        blob = activeSide === 'front' ? design.backBlob : design.frontBlob;
        loadedFromOtherSide = true;
      }

      // If we have a blob, create a fresh image layer from it
      // This ensures the blob URL is valid for the current session
      if (blob) {
        if (loadedFromOtherSide) {
          console.warn(`[EditorContext] Loading design ${designId} from other side blob`);
        }

        // Create fresh blob URL for this session
        const blobUrl = URL.createObjectURL(blob);

        // Load image to get dimensions
        const img = new Image();
        img.src = blobUrl;
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });

        // Create image layer centered on canvas
        const imageLayer: Layer = {
          id: `library-design-${Date.now()}`,
          name: 'Library Design',
          type: 'image',
          visible: true,
          locked: false,
          inverted: false,
          opacity: 1,
          data: {
            type: 'image',
            src: blobUrl,
            x: 0,
            y: 0,
            width: img.width,
            height: img.height,
            rotation: 0,
            flipH: false,
            flipV: false,
            originalWidth: img.width,
            originalHeight: img.height,
          },
        };

        // Clear existing user layers and add the library design
        clearUserLayers();
        addLayer(imageLayer);
        return;
      }

      // Fallback: If no blob data exists, try to load from stored layers
      // This handles legacy designs that might only have layer data
      const layersToLoad = activeSide === 'front' ? design.frontLayers : design.backLayers;
      if (layersToLoad) {
        console.warn(
          `[EditorContext] No blob data found, loading from stored layers for design ${designId}`
        );
        dispatch({ type: 'LOAD_DESIGN_LAYERS', payload: layersToLoad });
        return;
      }

      // No blob or layers available
      console.warn(`Design ${designId} has no blobs or layers - showing empty canvas`);
      clearUserLayers();
    },
    [dispatch, activeSide, clearUserLayers, addLayer]
  );

  const loadSnapshot = useCallback(
    async (snapshot: EditorSnapshot) => {
      // Wait for product layers to ACTUALLY be in state before loading snapshot
      // This prevents race condition where snapshot loads before product base/overlay
      const hasProductLayersInState = () => {
        const stateRef = activeSide === 'front' ? frontStateRef : backStateRef;
        return stateRef.current.layers.some((l) => l.id === PRODUCT_BASE_LAYER_ID);
      };

      // If product layers not yet in state, wait for them (max 5 seconds)
      if (!hasProductLayersInState()) {
        console.warn('[EditorContext] Waiting for product layers before loading snapshot...');
        const startTime = Date.now();
        const timeout = 5000;

        await new Promise<void>((resolve) => {
          const checkInterval = setInterval(() => {
            if (hasProductLayersInState() || Date.now() - startTime > timeout) {
              clearInterval(checkInterval);
              resolve();
            }
          }, 50);
        });

        if (!hasProductLayersInState()) {
          console.error('[EditorContext] Timeout waiting for product layers in state');
          throw new Error('Product layers not initialized');
        }
      }

      // Load layers from snapshot directly
      dispatch({ type: 'LOAD_DESIGN_LAYERS', payload: snapshot });
    },
    [dispatch, activeSide]
  );

  const saveToLibrary = useCallback(async (): Promise<string | null> => {
    // TODO: This method needs to be called from components that can export canvas as blobs
    // For now, we'll return null and let components handle blob export themselves
    // Components should:
    // 1. Call editor.getSnapshot() to get layer data
    // 2. Export their canvas as blob
    // 3. Call saveDesign() directly with both blob and snapshot

    console.warn(
      '[EditorContext] saveToLibrary called but blob export must be handled by component'
    );
    return null;
  }, []);

  // Auto-save on unmount (when navigating away from Manual mode)
  useEffect(() => {
    return () => {
      // Get current auth user (or use anonymous)
      const userId = auth?.user?.id || 'anonymous';

      // Only save if there are user layers (non-product layers)
      const userLayers = activeState.layers.filter(
        (layer) =>
          layer.id !== PRODUCT_BASE_LAYER_ID &&
          layer.id !== PRODUCT_OVERLAY_LAYER_ID &&
          layer.id !== PRODUCT_PRODUCTION_FRAME_ID
      );

      if (userLayers.length > 0) {
        const snapshot = getSnapshot();

        // Save auto-save entry
        saveAutoSave({
          id: crypto.randomUUID(),
          userId,
          productId: product.id,
          side: activeSide,
          snapshot,
          createdAt: new Date(),
          isManualSave: false,
        }).catch((error) => {
          console.error('[EditorContext] Failed to save auto-save on unmount:', error);
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run cleanup on unmount

  const value: EditorContextValue = {
    state,
    dispatch,
    setTool,
    setZoom,
    setPan,
    toggleGrid,
    toggleSnap,
    toggleGuides,
    resetView,
    addLayer,
    addLayersBatch,
    deleteLayer,
    duplicateLayer,
    reorderLayers,
    updateLayer,
    setActiveLayer,
    selectLayer,
    selectAllLayers,
    clearSelection,
    setAdjustment,
    setPenSize,
    setEraserSize,
    setPenColor,
    alignLayerHorizontal,
    alignLayerVertical,
    distributeLayers,
    undo,
    redo,
    canUndo: state.history.past.length > 0,
    canRedo: state.history.future.length > 0,
    getSnapshot,
    saveStatus: autoSave.status,
    saveNow: autoSave.saveNow,
    lastSaved: autoSave.lastSaved,
    saveToLibrary,
    loadFromLibrary,
    loadSnapshot,
    clearUserLayers,
    hasUserLayers,
    updateBaseLayerImage,
    orientation,
    setOrientation,
    registerThumbnailGenerator,
    generateThumbnail,
  };

  return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>;
}
